import type { V2PlanInfo } from "schemas/billing";
import type { BillingInterval } from "schemas/generated/billing";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { formatPrice } from "replo-utils/lib/math";
import { v2AdditionalShopPricePerMonth } from "schemas/billing";

type V2PlanPriceVariant = "base" | "header";

interface V2PlanPriceProps {
  plan: V2PlanInfo;
  billingInterval: BillingInterval;
  v2AdditionalShops: number;
  variant: V2PlanPriceVariant;
}

export const getV2PlanPricePerMonthIncludingAllProducts = ({
  plan,
  billingInterval,
  v2AdditionalShops,
}: {
  plan: V2PlanInfo;
  billingInterval: BillingInterval;
  v2AdditionalShops: number;
}) => {
  const isYearly = billingInterval === "yearly";

  const pricePerMonthForAdditionalShops =
    v2AdditionalShops * v2AdditionalShopPricePerMonth;

  const monthlyPriceForAllProducts =
    plan.monthlyPrice + pricePerMonthForAdditionalShops;

  const yearlyPricePerMonthForAllProducts =
    plan.yearlyPricePerMonth + pricePerMonthForAdditionalShops;

  const priceToDisplay = isYearly
    ? yearlyPricePerMonthForAllProducts
    : monthlyPriceForAllProducts;

  return priceToDisplay;
};

export const V2PlanPricePerMonthIncludingAllProducts = ({
  plan,
  billingInterval,
  v2AdditionalShops,
  variant,
}: V2PlanPriceProps) => {
  const priceToDisplay = getV2PlanPricePerMonthIncludingAllProducts({
    plan,
    billingInterval,
    v2AdditionalShops,
  });

  const formattedPrice = formatPrice(priceToDisplay);

  const variantClassName: Record<V2PlanPriceVariant, string> = {
    base: "text-lg font-medium",
    header: "text-3xl font-semibold",
  };

  const suffixClassName: Record<V2PlanPriceVariant, string> = {
    base: "text-lg font-medium",
    header: "text-base",
  };

  return (
    <span className={twMerge(variantClassName[variant])}>
      {formattedPrice}
      <span className={twMerge("font-normal", suffixClassName[variant])}>
        /month
      </span>
    </span>
  );
};
